import { FC } from "react";

import { StepLogLineStatus } from "./StepLogLineStatus";
import { IStepLogLine } from "./stepLog.types";
import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { stringAvatar } from "@/features/procedures/lib/checklist";
import {
	Avatar,
	Box,
	Chip,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const StepLogLine: FC<IStepLogLine> = (props) => {
	const theme = useTheme();
	const { timezone } = useTime();
	const date = dayjs.utc(props.logLine.Date).tz(timezone);
	const dateShort = date.format("DD/MM/YY");
	const timeShort = date.format("HH:mm:ss");

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "flex-start",
				gap: 1.5,
			}}
		>
			{/* Timeline dot + time */}
			<Stack
				alignItems="center"
				sx={{
					flexShrink: 0,
					width: 52,
					pt: 0.25,
				}}
			>
				<Box
					sx={{
						width: 8,
						height: 8,
						borderRadius: "50%",
						bgcolor: "info.main",
						boxShadow: (t) => `0 0 0 2px ${alpha(t.palette.info.main, 0.2)}`,
					}}
				/>
				<Typography
					variant="caption"
					sx={{
						mt: 0.5,
						color: "text.secondary",
						lineHeight: 1.3,
						textAlign: "center",
					}}
				>
					{dateShort}
				</Typography>
				<Typography
					variant="caption"
					sx={{
						color: "text.secondary",
						fontWeight: 500,
					}}
				>
					{timeShort}
				</Typography>
			</Stack>

			{/* Entry card: soft border, aligned with step style */}
			<Box
				sx={{
					flex: 1,
					minWidth: 0,
					py: 1.25,
					px: 1.5,
					borderRadius: 2,
					border: "1px solid",
					borderColor: alpha(theme.palette.divider, 0.3),
					bgcolor: alpha(theme.palette.background.paper, 0.5),
					transition: "border-color .2s ease, box-shadow .2s ease",
					"&:hover": {
						borderColor: alpha(theme.palette.divider, 0.5),
						boxShadow:
							theme.palette.mode === "dark"
								? "0 2px 8px rgba(0,0,0,0.15)"
								: "0 2px 8px rgba(0,0,0,0.04)",
					},
				}}
			>
				<Stack direction="row" alignItems="flex-start" spacing={1.25}>
					<Avatar
						{...stringAvatar(props.logLine.UserName)}
						alt={props.logLine.UserName}
						sx={{
							width: 28,
							height: 28,
							border: (t) => `1px solid ${alpha(t.palette.divider, 0.35)}`,
						}}
					/>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography
							variant="subtitle2"
							sx={{
								fontWeight: 600,
								lineHeight: 1.3,
								color: "text.primary",
							}}
						>
							{props.logLine.UserName}
						</Typography>
						<Box
							sx={{
								mt: 0.5,
								lineHeight: 1.5,
								color: "text.primary",
								"& p": { margin: 0 },
							}}
						>
							<SATOSContentRender content={props.logLine.Value} />
						</Box>
						<Chip
							icon={<StepLogLineStatus {...props} />}
							label={props.logLine.ArchiveType}
							variant="outlined"
							size="small"
							sx={{
								mt: 1,
								height: 22,
								borderRadius: 1.25,
								"& .MuiChip-label": { px: 0.75 },
								"& .MuiChip-icon": { fontSize: "inherit" },
							}}
						/>
					</Box>
				</Stack>
			</Box>
		</Box>
	);
};
